import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home, ChevronRight, Building2, Phone, Lock, MapPin, Eye, EyeOff,
  UserPlus, CheckCircle2, Sparkles, LogIn, User, LayoutGrid,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { CITIES } from "../mock/mockData";
import { useCategories } from "../context/CategoriesContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import { useOtpGate } from "../components/OtpGate";
import FirmaLoginDialog from "../components/FirmaLoginDialog";
import { formatPhone } from "../lib/phone";
import { districtsOf } from "../lib/districts";

const PERKS = [
  "Binlerce çiftin karşısına çıkın",
  "Ücretsiz teklif talepleri alın",
  "Panelinizden mekan bilgilerinizi ve fotoğraflarınızı yönetin",
];

const BusinessRegister = () => {
  const { toast } = useToast();
  const { register } = useAuth();
  const { gate, dialog: otpDialog } = useOtpGate("firm");
  const { categories } = useCategories();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", contactName: "", category: "", phone: "", password: "", password2: "", city: "", district: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.contactName || !form.category || !form.phone || !form.password || !form.city || !form.district) {
      toast({ title: "Eksik bilgi", description: "Lütfen tüm alanları doldurun." });
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      toast({ title: "Geçersiz telefon", description: "Telefon numarası 11 haneli olmalıdır (örn. 05xx xxx xx xx)." });
      return;
    }
    if (form.password.length < 4) {
      toast({ title: "Zayıf şifre", description: "Şifreniz en az 4 karakter olmalı." });
      return;
    }
    if (form.password !== form.password2) {
      toast({ title: "Şifreler eşleşmiyor", description: "Lütfen şifreyi doğru tekrar girin." });
      return;
    }
    setLoading(true);
    try {
      await gate(form.phone, async (otp) => {
        await register({ name: form.name, contactName: form.contactName, category: form.category, phone: form.phone, password: form.password, city: form.city, district: form.district, otp });
        toast({ title: "Kayıt başarılı!", description: "Şimdi panelinizden bilgilerinizi tamamlayın." });
        navigate("/firma-panel");
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Kayıt yapılamadı, tekrar deneyin.";
      toast({ title: "Hata", description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FBF5F0] min-h-screen">
      {otpDialog}
      <Header />

      {/* Banner */}
      <div className="relative pt-28 pb-14 overflow-hidden bg-gradient-to-br from-[#9E2A3B] to-[#7A1F2C]">
        <div className="relative z-10 max-w-6xl mx-auto px-5">
          <nav className="flex items-center gap-2 text-white/80 text-sm mb-3">
            <Link to="/" className="flex items-center gap-1 hover:text-white"><Home className="w-3.5 h-3.5" /> Anasayfa</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#E3CE9E]">İşletmeni Ekle</span>
          </nav>
          <h1 className="font-display text-3xl md:text-5xl font-bold text-white">İşletmeni Ücretsiz Ekle</h1>
          <p className="text-white/85 mt-2 max-w-2xl">
            Hemen hesabınızı oluşturun. Kategori, kapasite, fiyat, açıklama ve fotoğrafları üye olduktan sonra panelinizden kolayca ekleyebilirsiniz.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Register form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-[#F0DDE0] p-6 md:p-8">
            <div className="flex items-center gap-2 mb-6">
              <Building2 className="w-5 h-5 text-[#9E2A3B]" />
              <h2 className="font-display text-2xl text-[#2A2224]">Hesap Oluştur</h2>
            </div>

            <div className="grid grid-cols-1 gap-5">
              <div className="space-y-1.5">
                <Label>Firma / Mekan Adı *</Label>
                <div className="relative">
                  <Building2 className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Örn: Pinpark Kır Düğün Salonları" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" data-testid="register-name-input" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label>Ad Soyad *</Label>
                  <div className="relative">
                    <User className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input value={form.contactName} onChange={(e) => set("contactName", e.target.value)} placeholder="Yetkili adı soyadı" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" data-testid="register-contactname-input" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Kategori *</Label>
                  <div className="relative">
                    <LayoutGrid className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                    <Select value={form.category} onValueChange={(v) => set("category", v)}>
                      <SelectTrigger className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]" data-testid="register-category-select"><SelectValue placeholder="Kategori seçin" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label>Telefon * <span className="text-[#a8999b] font-normal">(giriş için)</span></Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input value={form.phone} data-testid="business-register-phone" onChange={(e) => set("phone", e.target.value.replace(/[^\d+ ]/g, "").slice(0, 16))} onBlur={() => set("phone", formatPhone(form.phone))} inputMode="tel" maxLength={16} placeholder="05xx xxx xx xx" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label>Şehir *</Label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                    <Select value={form.city} onValueChange={(v) => setForm((f) => ({ ...f, city: v, district: "" }))}>
                      <SelectTrigger data-testid="business-register-city" className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder="Şehir seçin" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5 md:col-start-2">
                  <Label>İlçe *</Label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                    <Select value={form.district} onValueChange={(v) => set("district", v)} disabled={!form.city}>
                      <SelectTrigger data-testid="business-register-district" className="pl-9 border-[#F0DDE0] focus:ring-[#C15B6B]"><SelectValue placeholder={form.city ? "İlçe seçin" : "Önce şehir seçin"} /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {districtsOf(form.city).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <Label>Şifre *</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input type={showPass ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="••••••••" className="pl-9 pr-10 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                    <button type="button" onClick={() => setShowPass((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7C6E70] hover:text-[#9E2A3B]" aria-label="Şifreyi göster">
                      {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Şifre (Tekrar) *</Label>
                  <div className="relative">
                    <Lock className="w-4 h-4 text-[#C15B6B] absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input type={showPass ? "text" : "password"} value={form.password2} onChange={(e) => set("password2", e.target.value)} placeholder="••••••••" className="pl-9 border-[#F0DDE0] focus-visible:ring-[#C15B6B]" />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex items-start gap-2 rounded-xl bg-[#FBF0F2] border border-[#F6E4E6] p-4">
              <Sparkles className="w-5 h-5 text-[#C6A15B] shrink-0 mt-0.5" />
              <p className="text-sm text-[#5b4d4f]">
                Kayıttan sonra <strong>panelinizden</strong> kapasite, başlangıç fiyatı, açıklama ve <strong>salon fotoğraflarınızı</strong> ekleyebilir; bilgileriniz anında sitede yayınlanır.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] text-white font-medium shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-70"
            >
              <UserPlus className="w-5 h-5" />
              {loading ? "Hesap oluşturuluyor..." : "Ücretsiz Kayıt Ol"}
            </button>

            <p className="text-center text-sm text-[#5b4d4f] mt-4">
              Zaten üye misiniz?{" "}
              <button type="button" onClick={() => setLoginOpen(true)} className="text-[#9E2A3B] font-medium hover:underline inline-flex items-center gap-1">
                <LogIn className="w-3.5 h-3.5" /> Giriş Yap
              </button>
            </p>
          </form>
        </div>

        {/* Perks sidebar */}
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 lg:sticky lg:top-24">
            <h3 className="font-display text-xl text-[#2A2224] mb-4">Neden DüğünSenin?</h3>
            <ul className="space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-[#5b4d4f]">
                  <CheckCircle2 className="w-5 h-5 text-[#9E2A3B] shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-5 rounded-xl bg-gradient-to-br from-[#F6E4E6] to-[#FBF0F2] p-4 text-center">
              <p className="font-display text-3xl text-[#9E2A3B]">1200+</p>
              <p className="text-sm text-[#7C6E70]">kayıtlı işletme</p>
            </div>
          </div>
        </aside>
      </div>

      <FirmaLoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
      <Footer />
    </div>
  );
};

export default BusinessRegister;
